import { createTheme } from '@mui/material/styles';

const defaultTheme = createTheme();
const pxToRem = defaultTheme.typography.pxToRem;

// ---------------------------------------------------------------------------
// RMDV Design Language tokens — implements docs/design/DESIGN_LANGUAGE.md
// + docs/design/specimen/ds.css. "Lego, not Duplo": dense, flat, crisp, one
// quiet accent. Light = azure, dark = indigo. White cards pop on a grey canvas.
// ---------------------------------------------------------------------------

// Raw ramps kept as a source for component customizations that import them.
export const brand = {
  50: 'hsl(214, 92%, 96%)',
  100: 'hsl(214, 92%, 92%)',
  200: 'hsl(214, 92%, 84%)',
  300: 'hsl(214, 90%, 68%)',
  400: 'hsl(214, 92%, 48%)',
  500: 'hsl(214, 92%, 42%)',
  600: 'hsl(214, 92%, 38%)',
  700: 'hsl(216, 92%, 32%)',
  800: 'hsl(218, 90%, 22%)',
  900: 'hsl(220, 90%, 16%)',
};

// Neutral greys (hue held but low saturation so they read as grey, not denim).
export const gray = {
  50: 'hsl(220, 18%, 97%)',
  100: 'hsl(220, 16%, 94%)',
  200: 'hsl(220, 15%, 88%)',
  300: 'hsl(220, 13%, 80%)',
  400: 'hsl(220, 11%, 64%)',
  500: 'hsl(220, 10%, 46%)',
  600: 'hsl(220, 10%, 38%)',
  700: 'hsl(220, 13%, 26%)',
  800: 'hsl(222, 24%, 12%)',
  900: 'hsl(224, 28%, 7%)',
};

export const green = {
  50: 'hsl(150, 55%, 95%)',
  100: 'hsl(150, 55%, 90%)',
  200: 'hsl(150, 50%, 78%)',
  300: 'hsl(150, 48%, 60%)',
  400: 'hsl(150, 60%, 42%)',
  500: 'hsl(150, 68%, 33%)',
  600: 'hsl(150, 70%, 28%)',
  700: 'hsl(150, 72%, 22%)',
  800: 'hsl(150, 75%, 15%)',
  900: 'hsl(150, 80%, 9%)',
};

export const orange = {
  50: 'hsl(40, 95%, 95%)',
  100: 'hsl(40, 95%, 90%)',
  200: 'hsl(38, 95%, 80%)',
  300: 'hsl(37, 95%, 62%)',
  400: 'hsl(36, 95%, 48%)',
  500: 'hsl(36, 95%, 40%)',
  600: 'hsl(34, 95%, 34%)',
  700: 'hsl(32, 95%, 27%)',
  800: 'hsl(30, 95%, 20%)',
  900: 'hsl(28, 95%, 13%)',
};

export const red = {
  50: 'hsl(0, 80%, 96%)',
  100: 'hsl(0, 80%, 92%)',
  200: 'hsl(0, 78%, 84%)',
  300: 'hsl(0, 75%, 70%)',
  400: 'hsl(0, 72%, 55%)',
  500: 'hsl(0, 72%, 47%)',
  600: 'hsl(0, 72%, 40%)',
  700: 'hsl(0, 72%, 32%)',
  800: 'hsl(0, 72%, 24%)',
  900: 'hsl(0, 72%, 15%)',
};

// Semantic palettes (light = azure accent, dark = indigo accent).
export const colorSchemes = {
  light: {
    palette: {
      primary: { light: 'hsl(214, 92%, 88%)', main: 'hsl(214, 92%, 48%)', dark: 'hsl(214, 92%, 42%)', contrastText: '#ffffff' },
      info: { light: 'hsl(214, 92%, 95%)', main: 'hsl(214, 92%, 48%)', dark: 'hsl(214, 92%, 42%)', contrastText: '#ffffff' },
      success: { light: 'hsl(150, 55%, 94%)', main: 'hsl(150, 68%, 33%)', dark: 'hsl(150, 68%, 27%)', contrastText: '#ffffff' },
      warning: { light: 'hsl(40, 95%, 93%)', main: 'hsl(36, 95%, 40%)', dark: 'hsl(34, 95%, 32%)', contrastText: '#ffffff' },
      error: { light: 'hsl(0, 80%, 96%)', main: 'hsl(0, 72%, 47%)', dark: 'hsl(0, 72%, 40%)', contrastText: '#ffffff' },
      grey: { ...gray },
      divider: 'hsl(220, 16%, 89%)',
      background: { default: 'hsl(220, 18%, 96%)', paper: '#ffffff' },
      text: {
        primary: 'hsl(222, 28%, 11%)',
        secondary: 'hsl(220, 10%, 38%)',
        disabled: 'hsl(220, 9%, 60%)',
        warning: 'hsl(36, 95%, 40%)',
      },
      action: {
        hover: 'hsl(220, 18%, 94%)',
        hoverOpacity: 0.06,
        selected: 'hsl(214, 92%, 95%)',
        selectedOpacity: 1,
        active: 'hsl(220, 10%, 38%)',
        focus: 'hsl(214, 92%, 90%)',
      },
      baseShadow: '0 1px 2px hsla(220, 30%, 12%, 0.07)',
    },
  },
  dark: {
    palette: {
      primary: { light: 'hsl(248, 55%, 28%)', main: 'hsl(248, 80%, 70%)', dark: 'hsl(248, 76%, 64%)', contrastText: '#ffffff' },
      info: { light: 'hsl(248, 50%, 20%)', main: 'hsl(248, 80%, 70%)', dark: 'hsl(248, 76%, 64%)', contrastText: '#ffffff' },
      success: { light: 'hsl(150, 35%, 15%)', main: 'hsl(150, 56%, 58%)', dark: 'hsl(150, 56%, 50%)', contrastText: '#0b0d11' },
      warning: { light: 'hsl(38, 45%, 15%)', main: 'hsl(38, 92%, 62%)', dark: 'hsl(38, 92%, 54%)', contrastText: '#0b0d11' },
      error: { light: 'hsl(0, 42%, 19%)', main: 'hsl(0, 78%, 68%)', dark: 'hsl(0, 78%, 60%)', contrastText: '#0b0d11' },
      grey: { ...gray },
      divider: 'hsl(224, 14%, 19%)',
      background: { default: 'hsl(224, 22%, 7%)', paper: 'hsl(224, 19%, 10%)' },
      text: {
        primary: 'hsl(220, 15%, 97%)',
        secondary: 'hsl(220, 12%, 64%)',
        disabled: 'hsl(220, 10%, 48%)',
        warning: 'hsl(38, 92%, 62%)',
      },
      action: {
        hover: 'hsl(224, 20%, 14%)',
        hoverOpacity: 0.1,
        selected: 'hsl(248, 50%, 20%)',
        selectedOpacity: 1,
        active: 'hsl(220, 12%, 64%)',
        focus: 'hsl(248, 50%, 24%)',
      },
      baseShadow: '0 1px 2px hsla(0, 0%, 0%, 0.4)',
    },
  },
};

// One dense, purposeful type scale (kills the 48/36/30 hole). Inter for UI.
export const typography = {
  fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontSize: 14,
  h1: { fontSize: pxToRem(28), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h2: { fontSize: pxToRem(24), fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.015em' },
  h3: { fontSize: pxToRem(20), fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h4: { fontSize: pxToRem(18), fontWeight: 600, lineHeight: 1.35 },
  h5: { fontSize: pxToRem(20), fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
  h6: { fontSize: pxToRem(16), fontWeight: 600, lineHeight: 1.4 },
  subtitle1: { fontSize: pxToRem(15), fontWeight: 500, lineHeight: 1.45 },
  subtitle2: { fontSize: pxToRem(13), fontWeight: 600, lineHeight: 1.4 },
  body1: { fontSize: pxToRem(14), fontWeight: 400, lineHeight: 1.5 },
  body2: { fontSize: pxToRem(14), fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: pxToRem(12), fontWeight: 400, lineHeight: 1.4 },
  button: { fontSize: pxToRem(14), fontWeight: 500, textTransform: 'none', letterSpacing: 0 },
  overline: { fontSize: pxToRem(11), fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.4 },
};

export const shape = {
  borderRadius: 8,
};

const defaultShadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];

export const shadows = defaultShadows;
